import React, { useEffect, useState } from 'react'
import {
  Box,
  Button,
  Container,
  Dialog,
  IconButton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { FragmentType, graphql, useFragment } from '@/generated/gql'
import { useLazyQuery } from '@apollo/client'
import { Close, HourglassBottom, HourglassTop, Tune } from '@mui/icons-material'
import moment from 'moment'
import MenuItems from './MenuItems'
import OrderedItemsGraphql from '../OrderedItemsGraphql'
import FiltersPanelMui from '../FiltersPanel/FiltersPanelMui'
import { CanteenCartItemInput } from '../MenuCart/types'
import { Meal } from '@/generated/gql/graphql'
import { useInterval } from 'react-use'
import { SPLITS, useFeatureFlag } from '@/helpers/useFeatureFlag'

const canteenMenuBodyUserDataFragment = graphql(`
  fragment CanteenMenuBody_UserDataFragment on User {
    ...CanteenMenuItems_UserDataFragment
    ...CanteenOrderedItems_UserDataFragment
  }
`)

const canteenMenuBodyCurrentMenuItemsQuery = graphql(`
  query CanteenMenuBody_CurrentMenuItemsQuery(
    $meal: Meal!
    $date: DateTime!
    $timeslotStart: DateTime!
    $mainsFilters: CCMenuItemFilters!
    $sidesFilters: CCMenuItemFilters!
    $drinksFilters: CCMenuItemFilters!
    $vendorFilters: CCVendorMenusQueryInput
  ) {
    me {
      id
      ccMenu(input: { meal: $meal, date: $date, timeslotStart: $timeslotStart }) {
        id
        mealServiceData {
          id
          ...CanteenOrderedItems_MenuDataFragment
          timeslots {
            id
            ordersOpenTime
            isPreOrdersOpenTime
            ordersAreOpen
            ordersCloseTime
            isPostOrdersCloseTime
            id
            allVendorMenus: vendorMenus {
              id
              ...CanteenMenuFiltersPanel_VendorMenuDataFragment
            }
            filteredVendorMenus: vendorMenus(input: $vendorFilters) {
              id
              vendor {
                id
                ...CanteenMenuItems_VendorDataFragment
                ...CanteenMenuItemCard_VendorDataFragment
              }
              mains: menuItems(limit: 200, filters: $mainsFilters) {
                cacheKeys
                pageInfo {
                  count
                }
                items {
                  id
                  ...CanteenMenuItems_MenuItemDataFragment
                }
              }
              sides: menuItems(limit: 200, filters: $sidesFilters) {
                cacheKeys
                pageInfo {
                  count
                }
                items {
                  id
                  ...CanteenMenuItems_MenuItemDataFragment
                }
              }
              drinks: menuItems(limit: 200, filters: $drinksFilters) {
                cacheKeys
                pageInfo {
                  count
                }
                items {
                  id
                  ...CanteenMenuItems_MenuItemDataFragment
                }
              }
            }
          }
        }
      }
    }
  }
`)

type Props = {
  meal: Meal
  date: string
  timeslot: string
  userData: FragmentType<typeof canteenMenuBodyUserDataFragment>
  cartItems: string[]
  menuFilters: { dietaries: string[]; allergens: string[]; vendors: string[] }
  ordersAttempted: number
  onClearFilters: () => void
  onToggleFilterValue: (category: 'vendors' | 'allergens' | 'dietaries', value: string) => void
  addToCart: (item: CanteenCartItemInput) => void
  onSuccessfulCancellation: () => Promise<void>
}

const MenuBody: React.FC<Props> = ({
  timeslot,
  date,
  meal,
  userData: userDataProp,
  cartItems = [],
  menuFilters,
  ordersAttempted,
  onSuccessfulCancellation,
  onToggleFilterValue,
  onClearFilters,
  addToCart,
}) => {
  const theme = useTheme()
  const useServerSideMenuClosedLogic = useFeatureFlag(
    SPLITS.USE_SERVER_SIDE_CANTEEN_MENU_CLOSED_LOGIC,
  )
  const [filtersOpen, setFiltersOpen] = useState(false)
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const dateISOString = moment(date).toISOString()
  const timeslotISOString = moment(timeslot).toISOString()
  const dietariesFiltersString = menuFilters.dietaries.join(',')
  const allergensFiltersString = menuFilters.allergens.join(',')
  const vendorFiltersString = menuFilters.vendors.join(',')
  const [runQuery, { data: menuData, loading, refetch, previousData: previousMenuData }] =
    useLazyQuery(canteenMenuBodyCurrentMenuItemsQuery)

  useInterval(() => {
    if (!menuData?.me.ccMenu) return
    const now = moment()
    const timeslotOrdersOpenTime =
      menuData?.me.ccMenu?.mealServiceData.timeslots[0]?.ordersOpenTime || ''
    const timeslotOrdersCloseTime =
      menuData?.me.ccMenu?.mealServiceData.timeslots[0]?.ordersCloseTime || ''
    const menuShouldBeOpen =
      now.isSameOrAfter(timeslotOrdersOpenTime) && now.isBefore(timeslotOrdersCloseTime)
    const menuIsOpenValue = Boolean(menuData.me.ccMenu.mealServiceData.timeslots[0]?.ordersAreOpen)
    if (menuShouldBeOpen !== menuIsOpenValue) {
      refetch()
    }
  }, 1000)

  useEffect(() => {
    if (!timeslotISOString) return
    const dietaries = dietariesFiltersString.length ? dietariesFiltersString.split(',') : []
    const allergens = allergensFiltersString.length ? allergensFiltersString.split(',') : []
    const vendors = vendorFiltersString.length ? vendorFiltersString.split(',') : []
    runQuery({
      variables: {
        meal,
        date: dateISOString,
        timeslotStart: timeslotISOString,
        mainsFilters: {
          menuItemType: ['main'],
          dietaries,
          allergens,
        },
        sidesFilters: {
          menuItemType: ['side'],
          dietaries,
          allergens,
        },
        drinksFilters: {
          menuItemType: ['drink'],
          dietaries,
          allergens,
        },
        vendorFilters: {
          ...(vendors.length && { includeVendorsById: vendors }),
        },
      },
      fetchPolicy: 'cache-and-network',
    })
  }, [
    dateISOString,
    meal,
    timeslotISOString,
    dietariesFiltersString,
    allergensFiltersString,
    vendorFiltersString,
    ordersAttempted,
    runQuery,
  ])

  const userData = useFragment(canteenMenuBodyUserDataFragment, userDataProp)

  const timeslotOrdersOpenTime =
    menuData?.me.ccMenu?.mealServiceData.timeslots[0]?.ordersOpenTime || ''
  const timeslotOrdersCloseTime =
    menuData?.me.ccMenu?.mealServiceData.timeslots[0]?.ordersCloseTime || ''

  const menuNotOpenForOrdersYet = useServerSideMenuClosedLogic
    ? Boolean(menuData?.me.ccMenu?.mealServiceData.timeslots[0]?.isPreOrdersOpenTime)
    : moment().isBefore(timeslotOrdersOpenTime)
  const menuCutOffPassed = useServerSideMenuClosedLogic
    ? Boolean(menuData?.me.ccMenu?.mealServiceData.timeslots[0]?.isPostOrdersCloseTime)
    : moment().isSameOrAfter(timeslotOrdersCloseTime)
  const timeslotOrdersOpen = useServerSideMenuClosedLogic
    ? Boolean(menuData?.me.ccMenu?.mealServiceData.timeslots[0]?.ordersAreOpen)
    : !menuNotOpenForOrdersYet && !menuCutOffPassed

  const menuItemData =
    menuData?.me.ccMenu?.mealServiceData.timeslots[0]?.filteredVendorMenus.map((menu) => {
      return {
        vendor: menu.vendor,
        mains: menu.mains.items,
        sides: menu.sides.items,
        drinks: menu.drinks.items,
      }
    }) || []

  const handleSuccessfulCancellation = async () => {
    refetch()
    await onSuccessfulCancellation()
  }

  return (
    <>
      {isMobile ? (
        <Stack direction="row-reverse" paddingX={3}>
          <Button
            startIcon={<Tune fontSize="small" />}
            sx={{ width: 'fit-content' }}
            size="small"
            onClick={() => setFiltersOpen(true)}
          >
            Filters
          </Button>
        </Stack>
      ) : null}

      <Stack
        spacing={3}
        paddingX={2}
        paddingY={2}
        paddingTop={isMobile ? 0 : 2}
        direction={{ md: 'row', xs: 'column' }}
      >
        {isMobile ? (
          <Dialog onClose={() => setFiltersOpen(false)} fullScreen open={filtersOpen}>
            <IconButton
              sx={{
                bgcolor: 'inverted.main',
                marginLeft: 'auto',
                maxWidth: 'fit-content',
                position: 'sticky',
                top: 0,
                zIndex: 3,
              }}
              onClick={() => setFiltersOpen(false)}
            >
              <Close />
            </IconButton>
            <Container>
              <FiltersPanelMui
                onClearFilters={onClearFilters}
                menuFilters={menuFilters}
                onToggleFilterValue={onToggleFilterValue}
                loading={loading}
                menuData={menuData?.me.ccMenu?.mealServiceData.timeslots[0]?.allVendorMenus}
              />
            </Container>
          </Dialog>
        ) : (
          <Box sx={{ minWidth: '260px', width: { xs: '100%', md: '260px' } }}>
            <FiltersPanelMui
              onClearFilters={onClearFilters}
              onToggleFilterValue={onToggleFilterValue}
              menuFilters={menuFilters}
              loading={loading}
              menuData={
                menuData?.me.ccMenu?.mealServiceData.timeslots[0]?.allVendorMenus ||
                previousMenuData?.me.ccMenu?.mealServiceData.timeslots[0]?.allVendorMenus
              }
            />
          </Box>
        )}
        <Box sx={{ width: '100%' }}>
          {menuData?.me.ccMenu?.mealServiceData ? (
            <OrderedItemsGraphql
              onSuccessfulCancellation={handleSuccessfulCancellation}
              userData={userData}
              menuData={menuData.me.ccMenu.mealServiceData}
            />
          ) : null}
          {loading || timeslotOrdersOpen ? (
            <MenuItems
              vendorMenus={menuItemData}
              loading={loading}
              cartItems={cartItems}
              addToCart={addToCart}
              userData={userData}
            />
          ) : (
            <Stack spacing={1} width="100%" alignItems="center">
              {menuNotOpenForOrdersYet ? (
                <HourglassTop sx={{ fontSize: '4rem' }} color="info" fontSize="large" />
              ) : null}
              {menuCutOffPassed ? (
                <HourglassBottom sx={{ fontSize: '4rem' }} color="info" fontSize="large" />
              ) : null}
              <Typography variant="subtitle2">
                This timeslot is {menuNotOpenForOrdersYet ? 'not open yet' : null}
                {menuCutOffPassed ? 'now closed' : null}
              </Typography>
              <Typography>
                {menuNotOpenForOrdersYet ? <>Check again after orders open</> : null}
                {menuCutOffPassed ? <>Try another date or timeslot</> : null}
              </Typography>
            </Stack>
          )}
        </Box>
      </Stack>
    </>
  )
}

export default MenuBody
